import { Activity, ShieldCheck, ShieldAlert, Layers } from "lucide-react";

/* ═══════════════════════════════════════════
   TRUST DIAL PILL (P1 Feb-2026)
   Compact, single-line trust dial badge shown
   inline on every record card in the review
   queue. Reads from the ``trust_dial`` field
   served by /api/datapool/batches/:id/records.

   Schema expected from the backend:
     {
       score: 0..100,
       reasons: ["weak_amount", ...],
       parser_source: "hsbc_specialist_v1" | "legacy_pipeline_v1" | ...,
       balance_check_passed: true | false | null,
       field_confidence: { overall: 0.78, ... }
     }
   ═══════════════════════════════════════════ */

const BAND = (score) => {
  if (score >= 85) return {
    border: "border-emerald-500/40",
    bg: "bg-emerald-500/[0.06]",
    text: "text-emerald-300",
    label: "text-emerald-400",
    Icon: ShieldCheck,
    band: "high",
  };
  if (score >= 70) return {
    border: "border-amber-500/40",
    bg: "bg-amber-500/[0.06]",
    text: "text-amber-300",
    label: "text-amber-400",
    Icon: ShieldAlert,
    band: "review",
  };
  return {
    border: "border-red-500/40",
    bg: "bg-red-500/[0.06]",
    text: "text-red-300",
    label: "text-red-400",
    Icon: ShieldAlert,
    band: "low",
  };
};

const REASON_LABELS = {
  weak_date: "weak date",
  weak_amount: "weak amount",
  currency_unresolved: "currency unresolved",
  direction_uncertain: "direction uncertain",
  balance_check_failed: "balance check failed",
  missing_evidence: "missing evidence",
  missing_description: "missing description",
  counterparty_unidentified: "counterparty unidentified",
};

const prettyReason = (raw) => REASON_LABELS[raw] || raw.replace(/_/g, " ");

export default function TrustDialPill({ trustDial, recordId }) {
  if (!trustDial || trustDial.score == null) return null;
  const score = Math.max(0, Math.min(100, Number(trustDial.score) || 0));
  const ui = BAND(score);
  const Icon = ui.Icon;
  const reasons = (trustDial.reasons || []).slice(0, 4);
  const parser = trustDial.parser_source || "legacy_pipeline_v1";
  const balance = trustDial.balance_check_passed;
  const balanceLabel =
    balance === true ? "passed" :
    balance === false ? "failed" :
    "n/a";
  const balanceClass =
    balance === true ? "text-emerald-400" :
    balance === false ? "text-red-400" :
    "text-slate-500";

  return (
    <div
      className={`mt-2 rounded-lg border ${ui.border} ${ui.bg} px-3 py-2`}
      data-testid={`trust-dial-${recordId}`}
      data-trust-dial-band={ui.band}
      data-trust-dial-score={score}
    >
      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        <div className="flex items-center gap-1.5">
          <Icon className={`w-3.5 h-3.5 ${ui.label}`} />
          <span className={`uppercase tracking-wider text-[9px] ${ui.label}`}>
            Trust Dial
          </span>
          <span
            className={`font-mono font-bold ${ui.text}`}
            data-testid={`trust-dial-score-${recordId}`}
          >
            {score}/100
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-400">
          <Layers className="w-3 h-3 text-slate-500" />
          <span className="text-slate-500">parser</span>
          <span
            className="font-mono text-slate-300"
            data-testid={`trust-dial-parser-${recordId}`}
          >
            {parser}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-400">
          <Activity className="w-3 h-3 text-slate-500" />
          <span className="text-slate-500">balance</span>
          <span
            className={`font-mono font-semibold ${balanceClass}`}
            data-testid={`trust-dial-balance-${recordId}`}
          >
            {balanceLabel}
          </span>
        </div>
      </div>
      {reasons.length > 0 && (
        <div
          className="mt-1.5 text-[10px] text-slate-400 leading-relaxed"
          data-testid={`trust-dial-reasons-${recordId}`}
        >
          <span className="text-slate-500 uppercase tracking-wider mr-1.5">
            Reasons:
          </span>
          {reasons.map((r, i) => (
            <span key={r} className="font-mono text-slate-300">
              {prettyReason(r)}
              {i < reasons.length - 1 ? <span className="text-slate-600 mx-1.5">·</span> : null}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
